import React, { useCallback, useState } from "react";
import {
	FiChevronDown,
	FiChevronUp,
	FiClock,
	FiColumns,
	FiCornerUpLeft,
	FiEye,
	FiPlus,
	FiPlusCircle,
	FiRotateCcw,
	FiRotateCw
} from "react-icons/fi";
import { MdDragIndicator } from "react-icons/md";
import { ComponentTypes, type All8PageContent } from "@bcc/core-renderers";
import { resolveComponentDefinition, type ComponentDefinition, type Path } from "../../Pages/pageComponentLibrary";
import { getComponentRuntimeKey } from "./pageEditorUtils";
import { COMPONENT_DRAG_MIME, insertionIndexFromPointer } from "./componentListUtils";
import PageEditorFieldControl from "./PageEditorFieldControl";
import PageEditorLayoutSectionEditor from "./PageEditorLayoutSectionEditor";
import PageEditorUnknownComponentCard from "./PageEditorUnknownComponentCard";
import PageEditorHistoryModal from "./PageEditorHistoryModal";
import type { ComponentHistoryEntry } from "./useComponentHistory";

type PageEditorComponentsPanelProps = {
	editable: boolean;
	previewComponents: All8PageContent;
	componentDefinitionMap?: Map<string, ComponentDefinition>;
	activeComponentKey: string | null;
	setActiveComponentKey: React.Dispatch<React.SetStateAction<string | null>>;
	updateComponentValue: (index: number, path: Path, value: any) => void;
	removeComponent: (index: number) => void;
	moveComponent: (from: number, to: number) => void;
	/** Inserts a deep copy of the component right below the original. Button hidden when absent. */
	duplicateComponent?: (index: number) => void;
	/** Opens the component library targeting this gap index (insertion position between cards). */
	onInsertAt?: (gapIndex: number) => void;
	onOpenLibrary: () => void;
	/** When provided, empty state shows "Start with the page header" button that adds the Page Header component. */
	onAddPageHeader?: () => void;
	allowImageVariables?: boolean;
	/** Session undo/redo. Buttons render only when the callbacks are provided. */
	onUndo?: () => void;
	onRedo?: () => void;
	canUndo?: boolean;
	canRedo?: boolean;
	/** Session history timeline for the « Historique » modal (dated, clickable). */
	historyEntries?: ComponentHistoryEntry[];
	historyIndex?: number;
	onJumpHistory?: (index: number) => void;
	/** Wraps the component at `index` into a new Container. Icon shown per top-level card. */
	onWrapInContainer?: (index: number) => void;
	/** Lifts a Container's children back to this level and removes it. Icon shown on Container cards. */
	onUnwrapContainer?: (index: number) => void;
	/** Nested rendering inside a Container: hides the outer toolbar/chrome and the wrap icon. */
	nested?: boolean;
	/** Extra controls injected into each card (e.g. a Container child's responsive settings). */
	renderCardExtras?: (index: number) => React.ReactNode;
};

/** Page components editing panel with drag-and-drop reordering, duplication, deletion and
 *  per-field editing. Cards expose their runtime key via `data-component-key` so external
 *  affordances (e.g. preview-to-card selection) can scroll them into view. */
const PageEditorComponentsPanel: React.FC<PageEditorComponentsPanelProps> = ({
	editable,
	previewComponents,
	activeComponentKey,
	setActiveComponentKey,
	updateComponentValue,
	removeComponent,
	moveComponent,
	duplicateComponent,
	onInsertAt,
	onOpenLibrary,
	onAddPageHeader,
	allowImageVariables,
	onUndo,
	onRedo,
	canUndo,
	canRedo,
	historyEntries,
	historyIndex,
	onJumpHistory,
	onWrapInContainer,
	onUnwrapContainer,
	nested,
	renderCardExtras
}) => {
	// Insertion position (0..length) the current drag would drop at; drives the blue line.
	const [dropIndicator, setDropIndicator] = useState<number | null>(null);
	// « Historique » modal (dated timeline) visibility.
	const [showHistory, setShowHistory] = useState(false);

	// Drags start from the HANDLE only (draggable is set on the handle, the card is used as the
	// drag image) : same approach as the site-layout editor, with a custom MIME so foreign drags
	// (text, files) never reorder anything.
	const handleDragStart = useCallback(
		(event: React.DragEvent, index: number) => {
			if (!editable) {
				event.preventDefault();
				return;
			}
			event.dataTransfer.effectAllowed = "move";
			event.dataTransfer.setData(COMPONENT_DRAG_MIME, JSON.stringify({ index }));
			const card = (event.currentTarget as HTMLElement).closest("[data-component-card]");
			if (card && typeof event.dataTransfer.setDragImage === "function") {
				event.dataTransfer.setDragImage(card, 24, 24);
			}
		},
		[editable]
	);

	const hasComponentDrag = (event: React.DragEvent) => {
		const types = event.dataTransfer?.types;
		// Some environments (tests, older engines) expose no types during dragover: stay permissive
		// there : the drop itself still requires a payload under our MIME.
		if (!types) return true;
		return Array.from(types).includes(COMPONENT_DRAG_MIME);
	};

	const handleDragOverCard = (event: React.DragEvent<HTMLDivElement>, index: number) => {
		if (!editable || !hasComponentDrag(event)) return;
		event.preventDefault();
		event.dataTransfer.dropEffect = "move";
		setDropIndicator(
			insertionIndexFromPointer(event.clientY, event.currentTarget.getBoundingClientRect(), index)
		);
	};

	const handleDropAt = (event: React.DragEvent, insertion: number) => {
		if (!editable) return;
		setDropIndicator(null);
		const raw = event.dataTransfer?.getData?.(COMPONENT_DRAG_MIME);
		if (!raw) return;
		event.preventDefault();
		try {
			const payload = JSON.parse(raw) as { index?: number };
			const from = payload?.index;
			if (typeof from !== "number") return;
			// Removing the dragged card first shifts every later position down by one.
			const to = insertion > from ? insertion - 1 : insertion;
			if (to !== from) moveComponent(from, to);
		} catch {
			// ignore malformed payloads
		}
	};

	const handleDropOnCard = (event: React.DragEvent<HTMLDivElement>, index: number) => {
		handleDropAt(
			event,
			insertionIndexFromPointer(event.clientY, event.currentTarget.getBoundingClientRect(), index)
		);
	};

	/** Slim boundary between/around cards: drop target with the blue insertion line, plus the
	 *  hover « + » (between cards only) that opens the library targeting this gap. */
	const renderGap = (gapIndex: number) => {
		const isEdge = gapIndex === 0 || gapIndex === previewComponents.length;
		const showInsert = editable && !!onInsertAt && !isEdge;
		return (
			<div
				key={`gap-${gapIndex}`}
				data-testid={`component-gap-${gapIndex}`}
				className={`group relative flex items-center justify-center ${isEdge ? "h-2" : "h-3"}`}
				onDragOver={(event) => {
					if (!editable || !hasComponentDrag(event)) return;
					event.preventDefault();
					event.dataTransfer.dropEffect = "move";
					setDropIndicator(gapIndex);
				}}
				onDrop={(event) => handleDropAt(event, gapIndex)}
			>
				{dropIndicator === gapIndex && (
					<div
						data-testid="component-drop-indicator"
						className="pointer-events-none absolute inset-x-1 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-blue-500"
						aria-hidden
					/>
				)}
				{showInsert && (
					<button
						type="button"
						aria-label="Insérer un composant ici"
						title="Insérer un composant ici"
						onClick={() => onInsertAt?.(gapIndex)}
						className="absolute z-10 inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 opacity-0 shadow-sm transition hover:border-blue-400 hover:text-blue-600 focus-visible:opacity-100 group-hover:opacity-100"
					>
						<FiPlus aria-hidden />
					</button>
				)}
			</div>
		);
	};

	// Prev/Next (session undo/redo) + « Historique ». Rendered only when wired.
	const hasHistory = !!onUndo || !!onRedo;
	const hasTimeline = !!onJumpHistory && !!historyEntries;
	const renderHistoryButtons = () =>
		hasHistory ? (
			<div className="flex items-center gap-1">
				<button
					type="button"
					onClick={onUndo}
					disabled={!editable || !canUndo}
					title="Annuler (précédent)"
					aria-label="Annuler"
					className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
				>
					<FiRotateCcw aria-hidden />
				</button>
				<button
					type="button"
					onClick={onRedo}
					disabled={!editable || !canRedo}
					title="Rétablir (suivant)"
					aria-label="Rétablir"
					className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
				>
					<FiRotateCw aria-hidden />
				</button>
				{hasTimeline && (
					<button
						type="button"
						onClick={() => setShowHistory(true)}
						title="Historique de la session"
						aria-label="Historique de la session"
						className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
					>
						<FiClock aria-hidden />
					</button>
				)}
			</div>
		) : null;

	return (
		<div className={nested ? "space-y-3" : "rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4"}>
			{!nested && (
				<div className="flex flex-wrap items-center justify-between gap-3">
					<div>
						<p className="text-xs uppercase tracking-[0.3em] text-slate-400">Components</p>
						<p className="text-sm text-slate-600">
							Build the page with reusable blocks. Edits instantly feed the live preview.
						</p>
					</div>
					<div className="flex flex-wrap items-center gap-2">
						<button
							type="button"
							onClick={onOpenLibrary}
							disabled={!editable}
							className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
						>
							<FiPlusCircle /> Add component
						</button>
						<button
							type="button"
							onClick={onOpenLibrary}
							disabled={!editable}
							className="inline-flex items-center gap-2 rounded-full border border-dashed border-slate-300 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
						>
							<FiEye /> Visual library
						</button>
						{/* Prev/Next (undo/redo) sit at the far right of the toolbar. */}
						{hasHistory && <span className="mx-1 h-6 w-px bg-slate-200" aria-hidden />}
						{renderHistoryButtons()}
					</div>
				</div>
			)}

			{previewComponents.length === 0 && (
				<div
					className={
						nested
							? "p-4 text-center text-slate-600"
							: "rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-slate-600"
					}
				>
					<p className="text-sm font-semibold">{nested ? "Container vide." : "No components yet."}</p>
					<p className="text-sm text-slate-500">
						{!editable
							? nested
								? "Activez l'édition pour ajouter des composants."
								: "Enable editing to add components."
							: nested
								? "Ajoutez un composant à ce Container."
								: "Open the library to add a page header, hero, news, stats, or any block."}
					</p>
					{editable && (
						<div className="mt-3 flex flex-wrap items-center justify-center gap-2">
							{onAddPageHeader && (
								<button
									type="button"
									onClick={onAddPageHeader}
									className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
								>
									<FiPlus /> Start with the page header
								</button>
							)}
							<button
								type="button"
								onClick={onOpenLibrary}
								className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
							>
								<FiPlusCircle /> {nested ? "Ajouter un composant" : "Add component"}
							</button>
						</div>
					)}
				</div>
			)}

			<div
				onDragLeave={(event) => {
					if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
						setDropIndicator(null);
					}
				}}
			>
				{previewComponents.map((component, index) => {
					const definition = resolveComponentDefinition(component.componentType as string);
					const componentKey = getComponentRuntimeKey(component.componentType as string, index);
					const isOpen = activeComponentKey === componentKey;
					// A Container IS the dotted region : its card wears the dashed border
					// directly (no card-within-a-card), and its body renders seamlessly.
					const isLayout = component.componentType === ComponentTypes.LayoutSection;
					return (
						<React.Fragment key={componentKey}>
							{renderGap(index)}
							<div
								data-component-card
								data-component-key={componentKey}
								className={
									isLayout
										? "rounded-2xl border-2 border-dashed border-[#005CA9]/40 bg-[#005CA9]/[0.02]"
										: "rounded-2xl border border-slate-200 bg-white shadow-sm"
								}
								onDragOver={(event) => handleDragOverCard(event, index)}
								onDrop={(event) => handleDropOnCard(event, index)}
							>
								<div
									className={`flex items-center justify-between gap-2 px-4 py-3 ${
										isLayout ? "" : "border-b border-slate-100"
									}`}
								>
									<div className="flex min-w-0 flex-1 items-center gap-2">
										{editable && (
											<span
												role="button"
												draggable
												aria-label="Réordonner"
												title="Réordonner (glisser-déposer)"
												onDragStart={(event) => handleDragStart(event, index)}
												onDragEnd={() => setDropIndicator(null)}
												className="shrink-0 cursor-grab select-none rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 active:cursor-grabbing"
											>
												<MdDragIndicator aria-hidden />
											</span>
										)}
										{/* Title only, truncated to a single line : the instance summary /
										    description are noise on the canvas; identity lives in the library. */}
										<p
											className="min-w-0 truncate text-xs font-semibold uppercase tracking-[0.2em] text-slate-500"
											title={definition?.title ?? (component.componentType as string)}
										>
											{definition?.title ?? component.componentType}
										</p>
									</div>
									{/* Right of the title, on the SAME line: only small icons (wrap/unwrap +
									    expand). The bulky move/duplicate/remove actions live in the open body. */}
									<div className="flex shrink-0 items-center gap-1">
										{editable && (
											<>
												{/* Wrap into a Container (top-level only : nesting is unsupported). */}
												{onWrapInContainer && !nested && component.componentType !== ComponentTypes.LayoutSection && (
													<button
														type="button"
														onClick={() => onWrapInContainer(index)}
														title="Placer dans un Container"
														aria-label="Placer dans un Container"
														className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-[#005CA9]/30 text-[#005CA9] transition hover:border-[#005CA9] hover:bg-[#005CA9]/5"
													>
														<FiColumns aria-hidden />
													</button>
												)}
												{/* Unwrap: lift this Container's children back out and remove it. */}
												{onUnwrapContainer && component.componentType === ComponentTypes.LayoutSection && (
													<button
														type="button"
														onClick={() => onUnwrapContainer(index)}
														title="Sortir du Container"
														aria-label="Sortir du Container"
														className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
													>
														<FiCornerUpLeft aria-hidden />
													</button>
												)}
											</>
										)}
										<button
											type="button"
											onClick={() =>
												setActiveComponentKey((current) => (current === componentKey ? null : componentKey))
											}
											className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
											aria-expanded={isOpen}
											aria-label={isOpen ? "Réduire le composant" : "Configurer le composant"}
										>
											{isOpen ? <FiChevronUp /> : <FiChevronDown />}
										</button>
									</div>
								</div>
								{renderCardExtras && (
									<div className="border-b border-slate-100 px-4 py-2">{renderCardExtras(index)}</div>
								)}
								{isOpen && (
									<div className={`space-y-3 ${isLayout ? "px-3 pb-3 pt-3" : "p-4"}`}>
										{/* Move / duplicate / remove live here (open state), keeping the
										    collapsed header a clean single line. */}
										{editable && (
											<div className="flex flex-wrap items-center gap-2">
												<button
													type="button"
													onClick={() => moveComponent(index, Math.max(0, index - 1))}
													disabled={index === 0}
													className="rounded-full border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
												>
													Monter
												</button>
												<button
													type="button"
													onClick={() => moveComponent(index, Math.min(previewComponents.length - 1, index + 1))}
													disabled={index === previewComponents.length - 1}
													className="rounded-full border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
												>
													Descendre
												</button>
												{duplicateComponent && (
													<button
														type="button"
														onClick={() => duplicateComponent(index)}
														className="rounded-full border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
													>
														Dupliquer
													</button>
												)}
												<button
													type="button"
													onClick={() => removeComponent(index)}
													className="rounded-full border border-rose-200 px-2.5 py-1 text-xs font-semibold text-rose-600 transition hover:border-rose-300 hover:bg-rose-50"
												>
													Supprimer
												</button>
											</div>
										)}
										{/* The « Type: » debug line is noise inside a Container : keep it seamless. */}
										{!isLayout && (
											<div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-xs font-mono text-slate-500">
												Type: {component.componentType}
											</div>
										)}
										{isLayout ? (
											// The Container's own card is already the dotted region; the editor
											// renders its settings + nested canvas directly, no extra chrome.
											<PageEditorLayoutSectionEditor
												editable={editable}
												componentIndex={index}
												previewComponents={previewComponents}
												updateComponentValue={updateComponentValue}
												allowImageVariables={allowImageVariables}
											/>
										) : definition ? (
											<div className="space-y-4">
												{definition.fields.map((field, fieldIndex) => (
													<div key={`${componentKey}-field-${fieldIndex}`}>
														<PageEditorFieldControl
															editable={editable}
															field={field}
															componentIndex={index}
															previewComponents={previewComponents}
															updateComponentValue={updateComponentValue}
															allowImageVariables={allowImageVariables}
														/>
													</div>
												))}
											</div>
											) : (
												<PageEditorUnknownComponentCard
													componentType={component.componentType as string}
													content={component.content}
												/>
											)}
									</div>
								)}
							</div>
						</React.Fragment>
					);
				})}
				{previewComponents.length > 0 && renderGap(previewComponents.length)}
				{editable && previewComponents.length > 0 && (
					// Persistent « add below » affordance: always visible under the last
					// component, so a new block can be added without opening the toolbar.
					<button
						type="button"
						data-testid="component-add-below"
						onClick={() => (onInsertAt ? onInsertAt(previewComponents.length) : onOpenLibrary())}
						className="mt-1 flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-slate-50/60 px-4 py-3 text-sm font-semibold text-slate-500 transition hover:border-blue-400 hover:bg-blue-50/60 hover:text-blue-600"
					>
						<FiPlusCircle aria-hidden /> Ajouter un composant
					</button>
				)}
			</div>

			{hasTimeline && (
				<PageEditorHistoryModal
					show={showHistory}
					onClose={() => setShowHistory(false)}
					entries={historyEntries ?? []}
					currentIndex={historyIndex ?? 0}
					onJump={(index) => onJumpHistory?.(index)}
				/>
			)}
		</div>
	);
};

export default PageEditorComponentsPanel;
